import { PointerEvent as ReactPointerEvent, RefObject, useCallback, useEffect, useRef } from 'react';

// a press turns into a drag only after this many px, so taps on buttons inside the dragged element still click
const DRAG_THRESHOLD = 8;

// while a drag is in progress the game iframe stops taking pointer events: a cross-origin iframe swallows
// the moves and the release once the pointer leaves the dragged element, leaving the drag stuck.
// Set on <body> on purpose - <html> class is matched exactly (`[class='is-web']`) all over the styles
const DRAG_ACTIVE_CLASS = 'drag-active';

interface UseVerticalDragOptions {
  /**
   * @description called on release with the new position as a fraction of the free vertical space
   * of the parent (0 - top, 1 - bottom); the element's own top has to be updated to it
   */
  onDragEnd: (position: number) => void;
}

interface UseVerticalDrag<T extends HTMLElement> {
  ref: RefObject<T>;
  onPointerDown: (event: ReactPointerEvent<T>) => void;
}

/**
 * @description drags an absolutely positioned element vertically inside its parent;
 * the element follows the pointer via transform (no re-renders while dragging)
 */
export const useVerticalDrag = <T extends HTMLElement>({ onDragEnd }: UseVerticalDragOptions): UseVerticalDrag<T> => {

  const ref = useRef<T>(null);
  const onDragEndRef = useRef(onDragEnd);
  const cancelRef = useRef<(() => void) | null>(null);

  onDragEndRef.current = onDragEnd;

  // unmounted mid-drag
  useEffect(() => () => cancelRef.current?.(), []);

  const onPointerDown = useCallback((event: ReactPointerEvent<T>) => {
    const node = ref.current;
    const parent = node?.parentElement;
    if (!node || !parent || !event.isPrimary || event.button !== 0) return;

    // typed non-null for the listeners below, narrowing does not reach into function declarations
    const element: T = node;
    const { pointerId, pointerType, clientY: startY } = event;
    const startTop = element.offsetTop;
    const maxTop = Math.max(0, parent.clientHeight - element.offsetHeight);
    let top = startTop;
    let dragging = false;

    function handleMove(moveEvent: PointerEvent) {
      if (moveEvent.pointerId !== pointerId) return;
      // the button was released where the launcher could not hear it
      if (pointerType === 'mouse' && !moveEvent.buttons) {
        finish(false);
        return;
      }
      const deltaY = moveEvent.clientY - startY;
      if (!dragging && Math.abs(deltaY) < DRAG_THRESHOLD) return;
      dragging = true;
      top = Math.min(Math.max(startTop + deltaY, 0), maxTop);
      element.style.transform = `translate3d(0, ${top - startTop}px, 0)`;
    }

    function handleUp(upEvent: PointerEvent) {
      if (upEvent.pointerId !== pointerId) return;
      finish(dragging);
    }

    function handleCancel() {
      finish(false);
    }

    function suppressClick(clickEvent: MouseEvent) {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
    }

    function finish(commit: boolean) {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
      window.removeEventListener('blur', handleCancel);
      document.body.classList.remove(DRAG_ACTIVE_CLASS);
      cancelRef.current = null;
      if (dragging) {
        // the click that follows the release ends the drag, it must not reach the button under the pointer
        window.addEventListener('click', suppressClick, true);
        setTimeout(() => window.removeEventListener('click', suppressClick, true), 50);
      }
      element.style.transform = '';
      // same task as the transform reset, so the new top is painted in the same frame (no jump back)
      if (commit) onDragEndRef.current(maxTop ? top / maxTop : 0);
    }

    document.body.classList.add(DRAG_ACTIVE_CLASS);
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleCancel);
    window.addEventListener('blur', handleCancel);
    cancelRef.current = handleCancel;
  }, []);

  return { ref, onPointerDown };
};
